import React from 'react';
import { deepCloneArray } from '../../../utils/array';
import { getId } from '../../../utils/item';
import { upsertItemGenerator, update } from '../../../services/item';
import {
  normalizeGeneratorConfig,
  sanitizeGeneratorConfigForStorage,
  sanitizeGeneratorEntriesForStorage,
} from '../../../utils/generator';

export const useGeneratorPersistence = ({ item, itemId, generatorId }) => {
  const persistGenerator = React.useCallback(
    nextGenerator => {
      if (!item?._id) {
        return;
      }
      const clonedGenerators = deepCloneArray(item?.generators || []);
      const sanitizedGenerators =
        sanitizeGeneratorEntriesForStorage(clonedGenerators);
      const idx = sanitizedGenerators.findIndex(obj => getId(obj) === generatorId);
      if (idx === -1) {
        return;
      }
      sanitizedGenerators[idx] = {
        [generatorId]: sanitizeGeneratorConfigForStorage(
          normalizeGeneratorConfig(nextGenerator, generatorId),
        ),
      };
      upsertItemGenerator(
        itemId,
        generatorId,
        sanitizedGenerators[idx][generatorId],
      ).catch(() => update(`items/${itemId}`, 'generators', sanitizedGenerators));
    },
    [item?._id, item?.generators, generatorId, itemId],
  );

  const withCurrentGenerator = React.useCallback(
    updater => {
      if (!item?._id) {
        return null;
      }
      const clonedGenerators = deepCloneArray(item?.generators || []);
      const idx = clonedGenerators.findIndex(obj => getId(obj) === generatorId);
      if (idx === -1) {
        return null;
      }
      const current = normalizeGeneratorConfig(
        clonedGenerators[idx]?.[generatorId] || {},
        generatorId,
      );
      const next =
        typeof updater === 'function'
          ? updater(current)
          : { ...current, ...updater };
      persistGenerator(next);
      return next;
    },
    [generatorId, item?._id, item?.generators, persistGenerator],
  );

  return { persistGenerator, withCurrentGenerator };
};
